import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { getBrandConfig } from '@/lib/configuration/configuration';
import { getContactDetailsFallbackInfo } from '@/lib/const';
import { messages } from '@/messages/boutique';
import { commonMessages } from '@/messages/common';
import { useRegionProvider } from '@/providers/RegionProvider';

interface ContactInfo {
  email: string;
  phoneNumber: string;
  weekdayHours: string;
  weekendHours: string;
}

export const useContactInfo = () => {
  const { formatMessage } = useIntl();
  const { country } = useRegionProvider();

  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    email: '',
    phoneNumber: '',
    weekdayHours: '',
    weekendHours: '',
  });

  const fetchContactInfo = async (countryCode: string) => {
    if (getBrandConfig().disableAlgoliaApiCall) return;
    try {
      const response = await fetch(
        `/api/contact-center/?countryCode=${countryCode}`,
      );

      if (!response.ok) {
        setContactInfo(getContactDetailsFallbackInfo());
        return;
      }

      const data = await response.json();

      const translateHours = (hours: string) => {
        return hours
          .replace('Mon-Fri', formatMessage(commonMessages.mondayToFriday))
          .replace('Saturday', formatMessage(commonMessages.saturday))
          .replace('Sunday', formatMessage(commonMessages.sunday))
          .replace('Sat - Sun', formatMessage(commonMessages.saturdayToSunday))
          .replaceAll('closed', formatMessage(messages.closed))
          .replace('open', formatMessage(messages.open));
      };

      const contactInfo: ContactInfo = {
        email: data.email,
        phoneNumber: data.phoneNumber,
        weekdayHours: translateHours(data.weekdayHours),
        weekendHours: translateHours(data.weekendHours),
      };

      setContactInfo(contactInfo);
    } catch (err) {
      setContactInfo(getContactDetailsFallbackInfo());
    }
  };

  useEffect(() => {
    if (country.code) {
      fetchContactInfo(country.code);
    }
  }, [country.code]);

  return contactInfo;
};
